/** Draft entry state per item and its conversion to and from the contract's `AssessmentAnswer` (assessment §1.3, §1.4). */
import type { AssessmentAnswer, AssessmentItemView } from "@bsim/contracts";

export interface Draft { readonly optionId: string | null; readonly entry: string; readonly confidence: number | null; readonly text: string }
export const EMPTY_DRAFT: Draft = { optionId: null, entry: "", confidence: null, text: "" };

/** assessment §1.3: prompt "How sure are you?" and the five labels. */
export const CONFIDENCE_PROMPT = "How sure are you?";
export const CONFIDENCE_LEVELS = [
  { value: 1, label: "Guessing" }, { value: 2, label: "Unsure" }, { value: 3, label: "Fairly sure" },
  { value: 4, label: "Confident" }, { value: 5, label: "Certain" },
] as const;

type NumericUnit = Extract<AssessmentItemView, { kind: "scenario_numeric" }>["unit"];

/** What the participant types, and how it is stored. $ is typed in $M and stored in dollars (assessment §1.4 A8). */
const UNITS: Readonly<Record<NumericUnit, { readonly suffix: string; readonly toStored: (typed: number) => number; readonly toTyped: (stored: number) => number }>> = {
  usd: { suffix: "$M", toStored: (m) => Math.round(m * 1_000_000), toTyped: (usd) => usd / 1_000_000 },
  bp: { suffix: "bp", toStored: (bp) => bp, toTyped: (bp) => bp },
  rate: { suffix: "%", toStored: (pct) => pct / 100, toTyped: (rate) => rate * 100 },
};

/** The line over a numeric answer: what the box expects. */
export const unitPrompt = (unit: NumericUnit): string =>
  unit === "usd" ? "Type your answer in millions of dollars ($M), for example 150 for $150M." : unit === "bp" ? "Type your answer in basis points (bp); 100bp is 1 percentage point." : "Type your answer as a percentage (%), for example 9.5 for 9.5%.";

export const unitSuffix = (unit: NumericUnit): string => UNITS[unit].suffix;

const parseTyped = (entry: string): number | null => {
  const cleaned = entry.replace(/[,\s]/g, "");
  const n = cleaned === "" ? Number.NaN : Number(cleaned);
  return Number.isFinite(n) ? n : null;
};

export function draftFromAnswer(item: AssessmentItemView, answer: AssessmentAnswer): Draft {
  if (item.kind === "scenario_numeric" && answer.kind === "scenario_numeric") return { ...EMPTY_DRAFT, entry: String(UNITS[item.unit].toTyped(answer.value)), confidence: answer.confidence };
  if (answer.kind === "scenario_mcq") return { ...EMPTY_DRAFT, optionId: answer.optionId, confidence: answer.confidence };
  if (answer.kind === "judgment_case") return { ...EMPTY_DRAFT, text: answer.text };
  return EMPTY_DRAFT;
}

/** A written answer past the item's own maximum is never saved or counted (the box shows how far over it is). */
export const isOverLength = (item: AssessmentItemView, draft: Draft): boolean => item.kind === "judgment_case" && draft.text.length > item.maxChars;
export const OVER_LENGTH_LINE = "Shorten your answer to save it.";

/** The answer to send, or null while the item is incomplete (a scored item needs its confidence too). */
export function answerFromDraft(item: AssessmentItemView, draft: Draft): AssessmentAnswer | null {
  if (item.kind === "judgment_case") return draft.text.trim() === "" || isOverLength(item, draft) ? null : { kind: "judgment_case", text: draft.text };
  if (draft.confidence === null) return null;
  if (item.kind === "scenario_mcq") return draft.optionId === null ? null : { kind: "scenario_mcq", optionId: draft.optionId, confidence: draft.confidence };
  const typed = parseTyped(draft.entry);
  return typed === null ? null : { kind: "scenario_numeric", value: UNITS[item.unit].toStored(typed), confidence: draft.confidence };
}

/** A scored item with its answer typed or chosen but no confidence clicked: it does not count yet, and the person is told what is missing. */
export const needsConfidence = (item: AssessmentItemView, draft: Draft): boolean =>
  item.kind !== "judgment_case" && draft.confidence === null && (item.kind === "scenario_mcq" ? draft.optionId !== null : parseTyped(draft.entry) !== null);
export const CONFIDENCE_MISSING_LINE = "Pick how sure you are to count this answer.";
